import { ProjectStoreApprovalStatus } from '@/lib/constants';
import { usePage } from '@inertiajs/react';
import { type ClassValue, clsx } from 'clsx';
import { escape } from 'html-escaper';
import moment from 'moment';
import React, { useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useMediaQuery } from 'usehooks-ts';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const useIsMobile = () => !useMediaQuery('(min-width: 768px)');

export const formatReadableDate = (date: string) =>
  moment(date).format('DD/MM/YYYY - HH:mm');

export const formatDecimal = (value: number, digits = 4) =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

export const useQueryParams = <T extends Record<string, unknown>>() => {
  const params = new URLSearchParams(window.location.search);
  return Object.fromEntries(params.entries()) as T;
};

export const useUser = () => usePage().props.auth.user!;

export const useIsAdmin = () => useUser().role === 'admin';

export const normalizeVietnamese = (str: string) =>
  str
    .replace(/([áàảãạâấầẩẫậăắằẳẵặ])/g, 'a')
    .replace(/([éèẻẽẹêếềểễệ])/g, 'e')
    .replace(/([íìỉĩị])/g, 'i')
    .replace(/([óòỏõọôốồổỗộơớờởỡợ])/g, 'o')
    .replace(/([úùủũụưứừửữự])/g, 'u')
    .replace(/([ýỳỷỹỵ])/g, 'y')
    .replace(/(đ)/g, 'd');

export const beautifyTextToHtml = (text: string) => {
  const escapedText = escape(text);

  return escapedText.replace(/\r?\n/g, '<br />');
};

export const approvalStatusDisplayText = (
  status: ProjectStoreApprovalStatus,
) => {
  switch (status) {
    case 'approved':
      return 'Đã duyệt';
    case 'new':
      return 'Chờ duyệt';
    case 'rejected':
      return 'Bị từ chối';
  }
};

type FormEventHandler = (e: React.FormEvent<HTMLFormElement>) => void;
export const preventDefaultFormSubmit =
  (handler: FormEventHandler) => (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handler(e);
  };

export const useChanged = <T,>(value: T, initialValue: T) => {
  const isChanged = useRef(false);
  if (!isChanged.current) {
    isChanged.current = value !== initialValue;
  }
  return isChanged.current;
};

export const useIsFirstRender = () => {
  const isFirstRender = useRef(true);
  if (isFirstRender.current) {
    isFirstRender.current = false;
    return true;
  }
  return false;
};

export const secondsToMoment = (seconds: number) =>
  moment().startOf('day').seconds(seconds);
